
import React, { useRef, useState, useEffect } from 'react';
import { Box, TextField, Typography } from '@mui/material';

type VerificationCodeProps = {
    onChange?: (code: string) => void;
    onComplete?: (code: string) => void;
    isError?: boolean;
    clearAttempts?: boolean;
};

const OTP_LENGTH = 6;

const shakeKeyframes = `@keyframes shake {
    0% { transform: translateX(0); }
    20% { transform: translateX(-6px); }
    40% { transform: translateX(6px); }
    60% { transform: translateX(-6px); }
    80% { transform: translateX(6px); }
    100% { transform: translateX(0); }
}`;

const VerificationCode: React.FC<VerificationCodeProps> = ({ onChange, onComplete, isError, clearAttempts }) => {
        const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
        const [hasCompleted, setHasCompleted] = useState(false);
        const [tooManyAttempts, setTooManyAttempts] = useState(false);

    useEffect(() => {
        if (clearAttempts) {
            localStorage.removeItem('otpAttempts');
            setTooManyAttempts(false);
        }
    }, [clearAttempts]);

    useEffect(() => {
        if (isError) {
            // Clear all inputs on error
            inputRefs.current.forEach(input => {
                if (input) input.value = '';
            });
            inputRefs.current[0]?.focus();
            setHasCompleted(false);
            
            // Update too many attempts logic
            setTooManyAttempts(prev => {
                const currentAttempts = (parseInt(localStorage.getItem('otpAttempts') || '0') + 1);
                localStorage.setItem('otpAttempts', currentAttempts.toString());
                return currentAttempts >= 3;
            });
        } else {
            // Reset attempts on successful verification or when error clears
            localStorage.removeItem('otpAttempts');
            setTooManyAttempts(false);
        }
    }, [isError]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = e.target;
        if (value.length > 1) {
            e.target.value = value.slice(0, 1);
        }
        const updatedValues = inputRefs.current.map((input) => input?.value || '').join('');
        if (updatedValues.length === OTP_LENGTH) {
            if (!hasCompleted) {
                onComplete?.(updatedValues);
            }
            setHasCompleted(true);
        } else {
            onChange?.(updatedValues);
        }
        if (value && index < inputRefs.current.length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !e.currentTarget.value && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text').slice(0, OTP_LENGTH);
        pasteData.split('').forEach((char, index) => {
            if (inputRefs.current[index]) {
                inputRefs.current[index]!.value = char;
                if (index < OTP_LENGTH - 1) {
                    inputRefs.current[index + 1]?.focus();
                }
            }
        });
        const updatedValues = inputRefs.current.map((input) => input?.value || '').join('');
        if (updatedValues.length === OTP_LENGTH) {
            onComplete?.(updatedValues);
            setHasCompleted(true);
        } else {
            onChange?.(updatedValues);
        }
    };

    return (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <style>{shakeKeyframes}</style>
                <Box
                    display="flex"
                    gap={2}
                    alignItems="center"
                    justifyContent="space-between"
                    onPaste={handlePaste}
                    width="100%"
                    sx={{
                        animation: isError ? 'shake 0.4s' : undefined,
                    }}
                >
                    {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                        <TextField
                            key={index}
                            inputRef={el => (inputRefs.current[index] = el)}
                            type="number"
                            inputProps={{ maxLength: 1, style: { textAlign: 'center', fontSize: 24, width: 55, height: 55, padding: 0 }, inputMode: "numeric", pattern: "[0-9]*" }}
                            onChange={e => handleInputChange(e as React.ChangeEvent<HTMLInputElement>, index)}
                            onKeyDown={e => handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, index)}
                            sx={{
                                '& .MuiInputBase-input': {
                                    borderRadius: 2,
                                    border: '1px solid #E8EBEC',
                                    background: 'var(--color-page-background)',
                                    boxShadow: isError ? '0 0 0 2px #ff5252' : 'none',
                                    transition: 'box-shadow 0.2s',
                                },
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                            }}
                        />
                    ))}
                </Box>
                {tooManyAttempts ? (
                    <Typography color="error" fontSize={12} textAlign="center">
                        Too many failed attempts. Please request a new code.
                    </Typography>
                ) : isError ? (
                    <Typography color="error" fontSize={12} textAlign="center">
                        Invalid code. Please try again
                    </Typography>
                ) : null}
            </Box>
    );
};

export default VerificationCode;